import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { MicroLessonWrapper } from '@/components/micro/MicroLessonWrapper';
import { useMicroSessionWithPrompts } from '@/hooks/useMicroSession';
import { Loader2 } from 'lucide-react';

interface MicroLessonPageProps {
  lessonId?: string;
}

const MicroLessonPageContent: React.FC<MicroLessonPageProps> = ({ lessonId }) => {
  const { microId } = useParams<{ microId: string }>();
  const location = useLocation();

  // Priority: prop > param > path extraction
  let finalMicroId = lessonId || microId;

  if (!finalMicroId) {
    const pathParts = location.pathname.split('/micro/');
    if (pathParts.length > 1) {
      finalMicroId = pathParts[1];
    }
  }

  const { session: micro, loading, error } = useMicroSessionWithPrompts(finalMicroId || '');

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          <p className="text-slate-500 text-sm">Loading lesson...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!micro) {
    return (
      <div className="p-8 text-center text-slate-500">
        <p>Micro lesson not found: {finalMicroId}</p>
        <p className="text-xs text-slate-400 mt-2">Path: {location.pathname}</p>
        {error && (
          <p className="text-xs text-red-400 mt-2">Error: {error.message}</p>
        )}
      </div>
    );
  }

  return <MicroLessonWrapper key={micro.id} micro={micro} />;
};

const MicroLessonPage: React.FC<MicroLessonPageProps> = (props) => {
  return <MicroLessonPageContent {...props} />;
};

export default MicroLessonPage;
